import {computed, DeepReadonly, readonly, Ref} from 'vue'
import {
  EventData,
  EventsConnectionState,
  EventsService as IEventsService,
  EventType,
  SubscribeOptions
} from '@hantera/portal-app/src/composition/use-events'
import {WebSocketClient} from './websocket-client'
import log from './log'

interface InternalSubscription {
  id: string
  path: string
  events: EventType[]
  snapshot?: boolean
  handler: (event: EventData) => void
}

export class EventsService implements IEventsService {
  private subscriptions = new Map<string, InternalSubscription>()
  private subscriptionIdCounter = 0
  private requestIdCounter = 0

  private readonly _state = computed<EventsConnectionState>(() => ({
    status: this.client.state.value
  }))

  constructor(private readonly client: WebSocketClient) {
    client.on('event', (msg) => this.handleEvent(msg))
    client.onAuthenticated(() => this.resubscribeAll())
  }

  get state(): DeepReadonly<Ref<EventsConnectionState>> {
    return readonly(this._state) as DeepReadonly<Ref<EventsConnectionState>>
  }

  subscribe(options: SubscribeOptions): () => void {
    const id = `sub-${++this.subscriptionIdCounter}`

    const internal: InternalSubscription = {
      id,
      path: options.path,
      events: options.events,
      snapshot: options.snapshot,
      handler: options.onEvent
    }

    this.subscriptions.set(id, internal)

    if (this.client.state.value === 'connected') {
      this.sendSubscribe([internal])
    }

    return () => {
      this.subscriptions.delete(id)
      if (this.client.state.value === 'connected') {
        this.client.send({
          type: 'unsubscribeEvents',
          requestId: this.nextRequestId(),
          ids: [id]
        })
      }
    }
  }

  private sendSubscribe(subs: InternalSubscription[]): void {
    this.client.send({
      type: 'subscribeEvents',
      requestId: this.nextRequestId(),
      subscriptions: subs.map(s => ({
        id: s.id,
        path: s.path,
        events: s.events,
        snapshot: s.snapshot
      }))
    })
  }

  private handleEvent(msg: Record<string, unknown>): void {
    const subscriptionIds = (msg.subscriptionIds as string[] | undefined) ?? []

    const eventData = {
      eventType: msg.eventType as EventType,
      path: msg.path as string,
      data: msg.data,
      timestamp: msg.timestamp as string
    } as EventData

    for (const subId of subscriptionIds) {
      const sub = this.subscriptions.get(subId)
      if (sub) {
        try {
          sub.handler(eventData)
        } catch (error) {
          log.error('[Events] Event handler error:', error)
        }
      }
    }
  }

  private resubscribeAll(): void {
    if (this.subscriptions.size === 0) return
    const subs = Array.from(this.subscriptions.values())
    log.debug(`[Events] Resubscribing ${subs.length} subscriptions`)
    this.sendSubscribe(subs)
  }

  private nextRequestId(): string {
    return `req-${++this.requestIdCounter}`
  }
}
